const HEIC_MIME_TYPES = ['image/heic', 'image/heif'];
const HEIC_EXTENSIONS = ['.heic', '.heif'];

const RAW_EXTENSIONS = ['.dng', '.raw', '.cr2', '.nef', '.arw', '.cr3', '.orf', '.rw2'];
const RAW_MIME_TYPES = ['image/x-adobe-dng', 'image/x-dcraw'];

export function isHeicFile(file: File): boolean {
  if (HEIC_MIME_TYPES.includes(file.type.toLowerCase())) {
    return true;
  }

  const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  return HEIC_EXTENSIONS.includes(ext);
}

export function isRawFile(file: File): boolean {
  if (RAW_MIME_TYPES.includes(file.type.toLowerCase())) {
    return true;
  }

  const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  return RAW_EXTENSIONS.includes(ext);
}

export async function convertHeicToJpeg(file: File): Promise<File> {
  let bitmap: ImageBitmap | null = null;

  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // Native decoding not supported (e.g. Chrome/Firefox) — fall through to heic-to
  }

  if (!bitmap) {
    return convertHeicWithLibrary(file);
  }

  let canvas: OffscreenCanvas | HTMLCanvasElement;
  let ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;

  if (typeof OffscreenCanvas !== 'undefined') {
    canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    ctx = canvas.getContext('2d');
  } else {
    canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    ctx = canvas.getContext('2d');
  }

  if (!ctx) {
    bitmap.close();
    throw new Error('Failed to create canvas context for HEIC conversion.');
  }

  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  let blob: Blob;

  if (canvas instanceof OffscreenCanvas) {
    blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.92 });
  } else {
    blob = await new Promise<Blob>((resolve, reject) => {
      (canvas as HTMLCanvasElement).toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob returned null.'))),
        'image/jpeg',
        0.92
      );
    });
  }

  const jpegName = file.name.replace(/\.(heic|heif)$/i, '.jpg');

  return new File([blob], jpegName, { type: 'image/jpeg' });
}

async function convertHeicWithLibrary(file: File): Promise<File> {
  try {
    const { heicTo } = await import('heic-to');
    const blob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 });
    const jpegName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    return new File([blob], jpegName, { type: 'image/jpeg' });
  } catch (e) {
    const detail = e instanceof Error ? e.message : JSON.stringify(e);
    throw new Error(`"${file.name}" is a HEIC image that could not be converted: ${detail}`);
  }
}
